import { ApplicationContext, ConfigurationsContext } from '../../App';
import {
	PalletizerContext,
	RuntimeContext,
	BayStatus,
} from '../../context/PalletizerContext';
import { useMachineConfig, getBarcodeStartModeEnabled } from '.././General';
import { Dot } from '.././General';
import { LayoutContent } from '.././Layout';
import Button from '.././PendantButton';
import { TabOption } from '.././Sidebar';
import { COLOR } from '.././shared/shared';
import { Bay, hasSlipSheet, PalletConfigObj, getSlipSheet } from 'blackbox';
import {
	Application,
	PickType,
	StackQueue,
	StackQueueItem,
	StackQueueStatus,
	SafetyStatus,
} from 'libapi';
import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { OperationScene } from '.././three/OperationScene';
import { isWebDeployment } from '.././shared/shared';
import CenterCameraIcon from '.././shared/CenterCameraIcon';
import { PalletizerStartDialog } from './StartDialog';
import { RobotControlMenu } from './RobotControl';
import { BayEditDialog, BaySelection } from './StackSelection';
import { PalletPane } from './InfoPanel';

export const lBlueFilter =
	'invert(16%) sepia(12%) saturate(2840%) hue-rotate(172deg) brightness(96%) contrast(91%)';

export function partitionObject(
	pObject: { [key: string]: any },
	pPartitions: number
): { [key: string]: any }[] {
	let lRes = [];
	let lAllKeys = Object.keys(pObject);
	if (lAllKeys.length <= pPartitions) return [pObject];
	const lPartitionSize = Math.floor(lAllKeys.length / pPartitions);
	let lKeyIdx = 0;
	for (let p = 0; p < pPartitions; p++) {
		const lPart: any = {};
		for (let i = 0; i < lPartitionSize; i++) {
			lPart[lAllKeys[lKeyIdx]] = pObject[lAllKeys[lKeyIdx]];
			lKeyIdx++;
		}
		lRes.push(lPart);
	}

	let pLeftOverIdx = 0;
	while (lKeyIdx < lAllKeys.length) {
		lRes[pLeftOverIdx][lAllKeys[lKeyIdx]] = pObject[lAllKeys[lKeyIdx]];
		lKeyIdx++;
		pLeftOverIdx = (pLeftOverIdx + 1) % pPartitions; // Circularly increment pLeftOverIdx
	}
	return lRes;
}

export interface Position {
	type: string;
	layer: number;
	position: number;
	id: string;
}

function getPositions(
	stackId: number,
	palletId: number,
	pPalletConfig: PalletConfigObj
) {
	if (
		stackId === undefined ||
		palletId === undefined ||
		stackId < 0 ||
		palletId < 0
	) {
		return [];
	}

	const positions: Position[] = [];
	const stack = pPalletConfig.getStack(palletId, stackId);
	stack.layers.forEach((layer, index) => {
		const layout = pPalletConfig.getLayout(palletId, layer.index);
		const slipsheet = getSlipSheet(
			index,
			stack.layers.length,
			pPalletConfig.config.slipsheets,
			stack.slipSheet
		);
		if (slipsheet)
			positions.push({
				type: 'Slipsheet',
				layer: index,
				position: 0,
				id: `slipsheet${index}`,
			});
		positions.push(
			...layout.boxPositions.map((_, i) => {
				return { type: 'Box', layer: index, position: i, id: `box${index}${i}` };
			})
		);
	});
	const topSlipSheet = getSlipSheet(
		stack.layers.length,
		stack.layers.length,
		pPalletConfig.config.slipsheets,
		stack.slipSheet
	);
	if (topSlipSheet)
		positions.push({
			type: 'Slipsheet',
			layer: stack.layers.length,
			position: 0,
			id: `slipsheet${stack.layers.length}`,
		});
	return positions;
}

function updateQueueItem(
	queueItem: StackQueueItem,
	position: number,
	positions: Position[]
) {
	const newPosition = positions[position];
	if (newPosition) {
		queueItem.LayerIndex = newPosition.layer;
		if (newPosition.type === 'Box') {
			queueItem.PickType = PickType.Box;
			queueItem.BoxIndex = newPosition.position;
		} else {
			queueItem.PickType = PickType.SlipSheet;
			queueItem.BoxIndex = 0;
		}
	}
	return queueItem;
}

interface SafetyStatusDotProps {
	status: SafetyStatus;
}

function SafetyStatusDot({ status }: SafetyStatusDotProps) {
	let color: COLOR;
	switch (status) {
		case SafetyStatus.Ready:
			color = COLOR.GREEN;
			break;
		case SafetyStatus.Triggered:
			color = COLOR.RED;
			break;
		case SafetyStatus.Resetting:
			color = COLOR.YELLOW;
			break;
		case SafetyStatus.Offline:
			color = COLOR.DARK_GREY;
			break;
		default:
			color = COLOR.DARK_GREY;
	}
	return <Dot pSize={18} pColor={color} pShadowColor={color} />;
}

enum PalletizerPageMode {
	NORMAL,
	EDIT_STACK,
	START,
	CONFIRM_STACK,
	INVALID_STACK,
}

interface EditingStackContext {
	BayID: number;
	BayTitle: string;
	InfeedID: number;
	StackIndex: number;
	PalletIndex: number;
	CurrentStack: StackQueueItem | undefined;
}

// Could be made into a general 'Page' props
export interface PageProps {
	pTab: TabOption;
}

export default function PalletizerPage({ pTab }: PageProps) {
	const [lMode, lSetMode] = useState<PalletizerPageMode>(
		PalletizerPageMode.NORMAL
	);

	const [lEditingStackContext, lSetEditingStackContext] =
		useState<EditingStackContext>({
			BayID: 0,
			BayTitle: '',
			InfeedID: 0,
			StackIndex: 0,
			PalletIndex: 0,
			CurrentStack: undefined,
		});

	const lApp = useContext(ApplicationContext) as Application;
	const lRuntimeContext = useContext(PalletizerContext) as RuntimeContext;
	const { all_configs: lConfigs } = useContext(ConfigurationsContext) as any;
	const lPalletConfig = PalletConfigObj.from(lConfigs as PalletConfigObj);
	const { leftBayStatus, rightBayStatus } = useContext(PalletizerContext);

	const [leftBaySelection, setLeftBaySelection] = useState<BaySelection | null>(
		null
	);
	const [rightBaySelection, setRightBaySelection] =
		useState<BaySelection | null>(null);
	const [leftPositions, setLeftPositions] = useState<Position[]>([]);
	const [rightPositions, setRightPositions] = useState<Position[]>([]);

	const [leftPosition, setLeftPosition] = useState<number>(-1);
	const [rightPosition, setRightPosition] = useState<number>(-1);

	const [recenter, setRecenter] = useState<number>(0);

	const [activeBay, setActiveBay] = useState<number>(-1);

	const lMachineConfig = useMachineConfig(lApp).lMachineConfig;
	const { version } = useContext(PalletizerContext);
	const lBarCodeScannerStartMode = getBarcodeStartModeEnabled();

	const [lBays, lSetBays] = useState<Bay[]>([]);
	useEffect(() => {
		if (!leftBaySelection) {
			setLeftPositions([]);
			setLeftPosition(-1);
			return;
		}
		setLeftPositions(
			getPositions(leftBaySelection.stack, leftBaySelection.pallet, lPalletConfig)
		);
	}, [leftBaySelection]);

	useEffect(() => {
		if (!rightBaySelection) {
			setRightPositions([]);
			setRightPosition(-1);
			return;
		}
		setRightPositions(
			getPositions(
				rightBaySelection.stack,
				rightBaySelection.pallet,
				lPalletConfig
			)
		);
	}, [rightBaySelection]);

	useEffect(() => {
		function updatePosition(
			selection: BaySelection,
			status: BayStatus,
			positions: Position[]
		) {
			if (!selection || !status || selection.pallet < 0 || selection.stack < 0)
				return -1;
			const positionStatus = status.position
				.split(',')
				.map(x => parseInt(x.split(':')[1]));

			const type = positionStatus[2] === 0 ? 'Box' : 'Slipsheet';
			const position = positions
				.filter(p => p.type === type)
				.filter(p => p.layer === positionStatus[1])[positionStatus[0]];
			let positionIndex = positions.indexOf(position);
			if (
				positionIndex === -1 &&
				type === 'Slipsheet' &&
				positions &&
				positions.length > 0 &&
				positions[positions.length - 1].type === 'Slipsheet'
			)
				positionIndex = positions.length - 1; // Backend is confused about the final layer ss
			return positionIndex >= 0 ? positionIndex : null;
		}

		const leftIndex = updatePosition(
			leftBaySelection!,
			leftBayStatus!,
			leftPositions
		);
		if (
			leftIndex !== null &&
			leftIndex !== undefined &&
			leftIndex !== leftPosition
		) {
			setLeftPosition(leftIndex);
		}

		const rightIndex = updatePosition(
			rightBaySelection!,
			rightBayStatus!,
			rightPositions
		);
		if (
			rightIndex !== null &&
			rightIndex !== undefined &&
			rightIndex !== rightPosition
		) {
			setRightPosition(rightIndex);
		}
	}, [
		leftBayStatus,
		rightBayStatus,
		leftBaySelection,
		rightBaySelection,
		leftPositions,
		rightPositions,
	]);

	useEffect(
		function () {
			if (!lMachineConfig) return;
			lSetBays(lMachineConfig?.Bays);
		},
		[lMachineConfig]
	);

	function isValidArrayIndex(pArr: any[], pIndex: number) {
		return pIndex >= 0 && pIndex < pArr.length;
	}

	function isValidQueueItem(
		pQueueItem: StackQueueItem,
		pConfig: PalletConfigObj
	): boolean {
		const lPallets = pConfig.config.pallets;
		return (
			isValidArrayIndex(lPallets, pQueueItem.PalletID) &&
			isValidArrayIndex(lPallets[pQueueItem.PalletID].Stack, pQueueItem.StackID) &&
			lPallets[pQueueItem.PalletID].Stack[pQueueItem.StackID].layers.length > 0
		);
	}

	const [lQueue, lSetQueue] = useState<StackQueue>([]);
	useEffect(
		function () {
			if (!isWebDeployment()) {
				lApp.api.Queue.get().then(function (pQueue) {
					if (!lPalletConfig) return;
					let lCp = [...pQueue];
					lCp = lCp.filter(sqi => isValidQueueItem(sqi, lPalletConfig));

					if (lCp.length !== pQueue.length) {
						console.error('One of the selected stacks became invalid.');
						clearQueue();
						lApp.stop();
						lSetQueue([]);
					} else {
						lSetQueue(lCp); // Prune sequence?
						const leftBay = lCp.find(sqi => sqi.BayID === 0);
						const rightBay = lCp.find(sqi => sqi.BayID === 1);
						if (leftBay)
							setLeftBaySelection({
								pallet: leftBay.PalletID,
								stack: leftBay.StackID,
							});
						else setLeftBaySelection(null);
						if (rightBay)
							setRightBaySelection({
								pallet: rightBay.PalletID,
								stack: rightBay.StackID,
							});
						else setRightBaySelection(null);
					}
				});
			}
		},
		[lRuntimeContext.StackQueueTimestamp, lPalletConfig]
	);

	function clearQueue() {
		lApp.api.Queue.post([]).catch(console.error);
		lApp.clearBPM();
		lApp.clearLeftBayStatus();
		lApp.clearRightBayStatus();
	}

	function publishQueueUpdatesOnStart(pAfterPost: () => void) {
		pAfterPost();

		// Set all queue to waiting.
		const lCp = [...lQueue];
		for (let i = 0; i < lCp.length; i++) {
			lCp[i].Status = StackQueueStatus.Waiting;
		}
		lSetQueue(lCp);
		console.warn('Posting to queue - pub updates on start', lCp);
		lApp.api.Queue.post(lCp)
			.then(function () {
				pAfterPost();
			})
			.catch(console.error);
	}

	function updateQueueStackItem(pNewStackItem: StackQueueItem) {
		if (!lQueue) return;
		// Only allow one stack per bay to be chosen!
		const lCp: StackQueue = [...lQueue];
		for (let i = 0; i < lCp.length; i++) {
			if (lCp[i].BayID === pNewStackItem.BayID) {
				lCp[i] = pNewStackItem;
				break;
			}
		}
		lSetQueue(lCp);
	}

	function removeFromQueue() {
		if (!lEditingStackContext) {
			return;
		}
		const { BayID: lBayID } = lEditingStackContext;

		const lCp: StackQueue = [...lQueue];
		for (let i = 0; i < lCp.length; i++) {
			if (lCp[i].BayID === lBayID) {
				lCp.splice(i, 1);
				break;
			}
		}

		if (lBayID === 0) lApp.clearLeftBayStatus();
		if (lBayID === 1) lApp.clearRightBayStatus();

		lSetQueue(lCp);
		console.warn('Removing from queue', lCp, lEditingStackContext);
		lApp.api.Queue.post(lCp)
			.then(function () {})
			.catch(console.error);
	}

	function getStackQueueItemForQueue(
		pNewPalletID: number,
		pNewStackID: number,
		pBayID: number,
		pInfeedID: number
	) {
		const lBase = {
			Status: 0,
			BayID: pBayID,
			PalletID: pNewPalletID,
			StackID: pNewStackID,
			StackUUID: '',
			LayerIndex: 0,
			SlipSheetsPlaced: 0,
			BoxIndex: 0,
			UIBoxIndex: 0,
			PickType: PickType.Box,
			InfeedID: pInfeedID,
		};
		if (lPalletConfig.getSSCountStack(pNewPalletID, pNewStackID) > 0) {
			const lStack = lPalletConfig.getStack(pNewPalletID, pNewStackID);
			if (hasSlipSheet(0, lStack.layers.length, lStack.slipSheet)) {
				lBase.PickType = PickType.SlipSheet;
			}
		}
		if (!lPalletConfig) {
			return {
				...lBase,
				StackUUID: String(uuidv4()),
			};
		} else {
			const lStackUUID =
				lPalletConfig.config.pallets[pNewPalletID].Stack[pNewStackID].id;
			return {
				...lBase,
				StackUUID: lStackUUID,
			};
		}
	}

	function updateQueue(
		pNewPalletID: number,
		pNewStackID: number,
		pEditingContext?: { [key: string]: number }
	) {
		let lNewStackItem;
		if (!pEditingContext) {
			lNewStackItem = getStackQueueItemForQueue(
				pNewPalletID,
				pNewStackID,
				lEditingStackContext.BayID,
				lEditingStackContext.InfeedID
			);
		} else {
			lNewStackItem = getStackQueueItemForQueue(
				pNewPalletID,
				pNewStackID,
				pEditingContext.BayID,
				pEditingContext.InfeedID
			);
		}

		const lCp: StackQueue = [...lQueue];
		let lModifiedQueue = false;
		for (let i = 0; i < lCp.length; i++) {
			if (lCp[i].BayID === lNewStackItem.BayID) {
				lCp[i] = lNewStackItem;
				lModifiedQueue = true;
				break;
			}
		}

		if (!lModifiedQueue) {
			lCp.push(lNewStackItem);
		}
		lCp.sort((s1, s2) => s1.BayID - s2.BayID);
		lSetQueue(lCp);
		lApp.api.Queue.post(lCp)
			.then(function () {})
			.catch(console.error);
	}

	// BARCODE RELATED
	const { pendingSKUScan, invalidSKUScan } = lRuntimeContext;
	useEffect(
		function () {
			if (!lBarCodeScannerStartMode) return;
			// Get stack from SKU
			// Update Queue
			let lInfeeds = Object.keys(pendingSKUScan);
			lInfeeds = lInfeeds.filter(inf => pendingSKUScan[inf] !== '');

			if (lInfeeds.length === 0) return;
			if (!lPalletConfig) {
				console.error(
					'Something went wrong. No pallet config when reading pending sku.'
				);
				lApp.stop();
				return;
			}

			const lTempQueue = [...lQueue];

			// Find the pending stacks and put them into the queue
			for (let i = 0; i < lInfeeds.length; i++) {
				const lInfeedID = Number(lInfeeds[i]);
				const lAlreadyHaveInQueue =
					lQueue.filter(pStackQueueItem => pStackQueueItem.InfeedID === lInfeedID)
						.length > 0;
				if (lAlreadyHaveInQueue) continue;
				const stack = lPalletConfig.findPalletIDAndStackIDFromSKUID(
					pendingSKUScan[lInfeedID]
				);

				if (!!stack) {
					const lBayIdxForInfeed = lBays.findIndex(b => b.InfeedIdx === lInfeedID);
					if (lBayIdxForInfeed >= 0) {
						lTempQueue.push(
							getStackQueueItemForQueue(
								stack.PalletID,
								stack.StackID,
								lBayIdxForInfeed,
								lInfeedID
							)
						);
						if (lInfeedID === 0) {
							setLeftBaySelection({
								stack: stack.StackID,
								pallet: stack.PalletID,
							});
						}
						if (lInfeedID === 1) {
							setRightBaySelection({
								stack: stack.StackID,
								pallet: stack.PalletID,
							});
						}
					}
				}
			}
			// Don't sort the queue here.
			// Show stacks as they are scanned in start dialog to avoid index flipping
			// if a sku is scanned while another is being confirmed.
			lSetQueue(lTempQueue);
			console.warn('Post to queue - Barcode pending sku scan', lTempQueue);
			lApp.api.Queue.post(lTempQueue)
				.then(function () {})
				.catch(console.error);
		},
		[pendingSKUScan]
	);

	useEffect(() => {
		if (!lBarCodeScannerStartMode) return;
		// Unknown sku takes precedence over known sku
		if (Object.values(invalidSKUScan).join('') !== '') {
			lSetMode(PalletizerPageMode.INVALID_STACK);
		} else if (
			Object.values(pendingSKUScan).join('') !== '' &&
			lQueue.length > 0
		) {
			lSetMode(PalletizerPageMode.CONFIRM_STACK);
		} else {
			lSetMode(PalletizerPageMode.NORMAL);
		}
	}, [lQueue, pendingSKUScan, invalidSKUScan]);

	const leftPalletFull = lRuntimeContext.palletizerIOs[0]
		? lRuntimeContext.palletizerIOs[0]['Pallet Full'] === 1
		: false;
	const rightPalletFull = lRuntimeContext.palletizerIOs[1]
		? lRuntimeContext.palletizerIOs[1]['Pallet Full'] === 1
		: false;

	return (
		<LayoutContent pTab={pTab} hideHeader={true}>
			<>
				<div className="flex-col flex-start vw-100 vh-91 full-width align-center">
					<div className="full-height full-width flex-row align-center flex-center">
						{/* Fetch number of bays. For each bay, render a vis. */}
						{lPalletConfig &&
							lBays.map((pBay, pIndex) => {
								// Important to iterate through all so pIndex matching the bay.
								if (!pBay.Enabled) return void 0;
								const lStackForBay = lQueue.filter(
									(pStackQueueItem: StackQueueItem) => pStackQueueItem.BayID === pIndex
								)[0];

								let lInfeedName = '';
								let lSelectedStackIndexInConfigs = -1;
								if (!!lStackForBay) {
									lInfeedName =
										lMachineConfig?.Infeeds[pBay.InfeedIdx] || 'Unmapped Infeed';
									if (!!lPalletConfig && lPalletConfig.config) {
										const lAllConfigStacks =
											lPalletConfig.config.pallets[lStackForBay.PalletID].Stack;
										for (let i = 0; i < lAllConfigStacks.length; i++) {
											if (i === lStackForBay.StackID) {
												lSelectedStackIndexInConfigs = i;
												break;
											}
										}
									}
								}

								const lTitle = pBay.Name + ' Pallet';

								const lPalletID = !!lStackForBay ? lStackForBay.PalletID : 0;
								if (pIndex === 0 && !leftBaySelection) {
									setLeftBaySelection({
										stack: lSelectedStackIndexInConfigs,
										pallet: lPalletID,
									});
								}
								if (pIndex === 1 && !rightBaySelection) {
									setRightBaySelection({
										stack: lSelectedStackIndexInConfigs,
										pallet: lPalletID,
									});
								}

								return (
									<React.Fragment key={pIndex}>
										<PalletPane
											key={pIndex}
											pTitle={lTitle}
											pSelectedStackItem={lStackForBay}
											pPalletConfig={lPalletConfig}
											pIODict={lRuntimeContext.palletizerIOs[pIndex]}
											pInvokeEditBay={() => {
												lSetEditingStackContext({
													BayID: pIndex,
													BayTitle: lTitle,
													InfeedID: pBay.InfeedIdx,
													StackIndex: lSelectedStackIndexInConfigs,
													PalletIndex: lPalletID,
													CurrentStack: lStackForBay,
												});
												lSetMode(PalletizerPageMode.EDIT_STACK);
												setActiveBay(pIndex);
											}}
											pBayID={pIndex}
											pBPM={lRuntimeContext.genericState?.bpm?.[`${pIndex}`]}
											positions={pIndex === 0 ? leftPositions : rightPositions}
											positionIndex={pIndex === 0 ? leftPosition : rightPosition}
											palletFull={pIndex === 0 ? leftPalletFull : rightPalletFull}
										/>
									</React.Fragment>
								);
							})}
					</div>
					<div
						style={{
							width: '100%',
							height: '90vh',
							position: 'absolute',
							top: '8vh',
						}}
					>
						<OperationScene
							palletConfig={lPalletConfig}
							leftPosition={leftPalletFull ? undefined : leftPosition + 1}
							leftUseHighlights={leftPalletFull ? false : true}
							leftStackIndex={
								leftBaySelection && leftBaySelection.stack >= 0
									? leftBaySelection.stack
									: undefined
							}
							leftPalletIndex={
								leftBaySelection && leftBaySelection.pallet >= 0
									? leftBaySelection.pallet
									: undefined
							}
							rightPosition={rightPalletFull ? undefined : rightPosition + 1}
							rightUseHighlights={rightPalletFull ? false : true}
							rightStackIndex={
								rightBaySelection && rightBaySelection.stack >= 0
									? rightBaySelection.stack
									: undefined
							}
							rightPalletIndex={
								rightBaySelection && rightBaySelection.pallet >= 0
									? rightBaySelection.pallet
									: undefined
							}
							recenter={recenter}
						/>
					</div>
					<div className="full-width grid-col-30-40" style={{ zIndex: 4 }}>
						<div className="full-size">
							<div
								onClick={() => setRecenter(prev => prev + 1)}
								style={{
									border: '2px solid #f1f5f9',
									width: '6vw',
									height: '5vw',
									position: 'absolute',
									bottom: '16pt',
									left: '1em',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									borderRadius: '8pt',
									paddingLeft: '0.5em',
									paddingRight: '0.5em',
								}}
							>
								<CenterCameraIcon />
								<b style={{ paddingTop: '0.5em', fontFamily: 'Larsseit' }}>
									Default View
								</b>
							</div>
						</div>
						<RobotControlMenu
							pHandleRunInfeed={() => {
								lApp.runInfeedManual();
							}}
							pStartEnabled={lQueue.length > 0 || !!lBarCodeScannerStartMode}
							pHandleHome={() => {
								lApp.home();
							}}
							pHandleStart={() => {
								if (lBarCodeScannerStartMode) {
									console.warn('Clearing queue - barcode start mode');
									clearQueue();
									lApp.startScanning(false);
								} else if (lQueue.length > 0) {
									lSetMode(PalletizerPageMode.START);
								} else {
									console.error('No queue available for start.');
								}
							}}
							pHandleStop={() => {
								lApp.stop();
							}}
							webDeploymentOverride={isWebDeployment() && lQueue.length > 0}
						/>
						<div className="full-size double-margin-top grid-col-70-30">
							<div className="flex-col">
								{Object.keys(lRuntimeContext.safety).map(function (name, index) {
									if (lRuntimeContext.safety[name] === SafetyStatus.Ready) return null;
									const status_name = SafetyStatus[lRuntimeContext.safety[name]];
									return (
										<div className="flex-col padding-left half-padding-top" key={index}>
											<div className="flex-row margin-top" key={index}>
												<SafetyStatusDot status={lRuntimeContext.safety[name]} />
												<span className="padding-left">
													{`${name} Safety:`} <b> {status_name} </b>
												</span>
											</div>
										</div>
									);
								})}
							</div>
							<div className="full-height-minus-2pad align-end full-width-minus-pad flex-end">
								<span className="font-1 bold-font color-dark-grey">{version}</span>
							</div>
						</div>
					</div>
				</div>
				{lMode === PalletizerPageMode.EDIT_STACK && (
					// TODO: Make tab optional
					<LayoutContent pTab={{ Name: '', IconClass: '' }} dialog={true}>
						<BayEditDialog
							title={lEditingStackContext.BayTitle}
							palletConfig={lPalletConfig}
							initialIndex={lEditingStackContext.StackIndex}
							initialPalletIndex={lEditingStackContext.PalletIndex}
							updateQueue={updateQueue}
							endEditing={() => {
								lSetMode(PalletizerPageMode.NORMAL);
								lApp.clearBPM();
							}}
							clearSelection={() => {
								removeFromQueue();
								lSetMode(PalletizerPageMode.NORMAL);
							}}
							bay={activeBay}
							updateLeft={selection => {
								if (
									selection &&
									(selection.pallet != leftBaySelection?.pallet ||
										selection.stack != leftBaySelection?.stack)
								) {
									lApp.clearLeftBayStatus(); // If we choose a new stack, clear the mqtt position status
								}
								setLeftBaySelection(selection);
							}}
							updateRight={selection => {
								if (
									selection &&
									(selection.pallet != rightBaySelection?.pallet ||
										selection.stack != rightBaySelection?.stack)
								) {
									lApp.clearRightBayStatus(); // If we choose a new stack, clear the mqtt position status
								}
								setRightBaySelection(selection);
							}}
						/>
					</LayoutContent>
				)}
				{(lMode === PalletizerPageMode.START ||
					lMode === PalletizerPageMode.CONFIRM_STACK) && (
					<LayoutContent pTab={{ Name: '', IconClass: '' }} dialog={true}>
						{/* FILTER OUT STACKS IN QUEUE NOT IN PENDING!!! */}
						<PalletizerStartDialog
							bays={lBays}
							queue={lQueue}
							palletConfig={lPalletConfig}
							updateQueueStackItem={updateQueueStackItem}
							exitStart={pAbort => {
								if (pAbort) {
									lApp.clearPendingSKUScan(pendingSKUScan);
									lApp.stop();
									lSetMode(PalletizerPageMode.NORMAL);
									console.warn('Clearing queue - Exit start dialog');
									clearQueue();
								} else {
									publishQueueUpdatesOnStart(() => {
										lSetMode(PalletizerPageMode.NORMAL);
									});
								}
							}}
							handleStart={(pStartBay, pPalletIdxs) => {
								lApp.clearPendingSKUScan(pendingSKUScan);
								lApp.start(pStartBay, pPalletIdxs);
							}}
							waitingForConfirm={lMode === PalletizerPageMode.CONFIRM_STACK}
							leftPositions={leftPositions}
							rightPositions={rightPositions}
							initialLeftPosition={leftPosition}
							initialRightPosition={rightPosition}
							updateQueueItem={updateQueueItem}
							pendingSKUScan={pendingSKUScan}
						/>
					</LayoutContent>
				)}
				{lMode === PalletizerPageMode.INVALID_STACK && (
					<LayoutContent pTab={{ Name: '', IconClass: '' }} dialog={true}>
						<div className="full-size flex-col align-center flex-around">
							<div className="flex-col align-center">
								<h3 className="font-7 color-blue bold-font">Unknown SKU scanned</h3>
								<div className="padding" />
								<span className="font-5 color-blue ">
									Please stop and add the SKU to your configuration before continuing.
								</span>
							</div>
							<span className="font-5 color-blue">{`Unknown SKU(s): ${Object.values(
								invalidSKUScan
							).join(', ')}`}</span>
							<Button
								pAction={() => {
									lApp.clearPendingSKUScan(pendingSKUScan);
									lApp.clearInvalidSKUScan(invalidSKUScan);
									console.warn('Clearning queue - invalid stack dialog');
									clearQueue();
									lSetMode(PalletizerPageMode.NORMAL);
									lApp.stop();
								}}
								pNormalColorOverride={COLOR.DARK_GREY}
								pClickColorOverride={COLOR.RED}
								pClass="double-margin-right margin-top"
							>
								<span className="font-6 bold-font double-padding">STOP</span>
							</Button>
						</div>
					</LayoutContent>
				)}
			</>
		</LayoutContent>
	);
}
